import { AxiosError } from "axios";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { ApiResources } from "@/api/resources";
import useTableDataFunctions from "@/components/Table/hooks/useTableDataFunctions";
import { ALERT_STATUS } from "@/context/alert/types";
import { COMPANIES_TABLE_COLUMNS } from "@/Pages/Companies/config";
import { DRIVERS_TABLE_COLUMNS } from "@/Pages/Drivers/config";
import {
  DRIVERS_FILES_TABLE_COLUMNS,
  VEHICLES_FILES_TABLE_COLUMNS
} from "@/Pages/Files/config";
import { ANALYSIS_PROVIDER_TABLE_COLUMNS } from "@/Pages/Integrations/AnalysisProviders/config";
import { INTEGRATIONS_TABLE_COLUMNS } from "@/Pages/Integrations/config";
import { INTEGRATIONS_API_TABLE_COLUMNS } from "@/Pages/Integrations/IntegrationsApi/config";
import { USERS_TABLE_COLUMNS } from "@/Pages/Users/config";
import { USERS_INVITATIONS_TABLE_COLUMNS } from "@/Pages/Users/UsersInvitations/config";
import {
  mapAnalysisProvidersTableRowData,
  mapCompaniesTableRowData,
  mapDriversFilesTableRowData,
  mapDriversTableRowData,
  mapIntegrationsApiTableRowData,
  mapIntegrationsTableRowData,
  mapUsersInvitationsTableRowData,
  mapUserTableRowData,
  mapVehicleFilesTableRowData,
  mapVehiclesTableRowData
} from "@/Pages/utils";
import { VEHICLES_TABLE_COLUMNS } from "@/Pages/Vehicles/config";
import { buildQueryString, Column, ROUTES } from "@/shared";

import { useDetailsStore } from "./useDetailsStore";

interface TableParams {
  page: string;
  pageNumber: string;
  showPerPage: string;
  selectedTableItem: any;
  contains: string | null;
  desktopDetailsSection: boolean;
  filters: any;
  sortedColumn: { orderBy: string; descending: boolean } | null;
  selectedTableItemUrlKey: string | null;
  hideFilters: boolean;
}

interface PaginationParams {
  page: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  hasPrevious: boolean;
  hasNext: boolean;
  autoAssignVrnEnabled?: boolean;
}

export interface TableStore<T> {
  data: T[];
  loading: boolean;
  initialLoading: boolean;
  pagination: PaginationParams;
  params: TableParams;
  initialized: boolean;
  noResults: boolean;
  inlineActions: any[];
  rowSwitch: any;
  isCheckable: boolean;
  columns: Column[];
  selectedRows: string[];
  selectableRows: any[];
  detailsData: any;
  closeDetails: () => void;
  setDetailsData: (detailsData: any) => void;
  setData: (data: T[]) => void;
  setDataAndPagination: (data: T[], paginationData: PaginationParams) => void;
  setLoading: (loading: boolean) => void;
  setColumns: (columns: Column[]) => void;
  setParams: (updates: Partial<TableParams>) => void;

  sortColumn: (resource: string) => void;
  selectRow: (rowId: string) => void;
  deselectRow: (rowId: string) => void;
  setSelectedRows: (rowIds: string[]) => void;
  clearSelection: () => void;
  setSelectableRows: (rowIds: string[]) => void;
  fetchData: (
    getData: (url: string) => Promise<any>,
    handleResponse: (
      status: ALERT_STATUS,
      message: string,
      description?: string
    ) => any
  ) => Promise<void>;
  setInlineActions: (actions: any[]) => void;
  setRowSwitch: (rowAction: any) => void;
  setIsCheckable: (checkable: boolean) => void;
  resetInitialLoading: () => void;
  resetFiltersAndDetailsData: (searchValue: string | null) => void;
  resetTableDetails: () => void;
  resetExceptPaginationAndColumns: () => void;
  reset: () => void;
  hardReset: () => void;
  autoAssignVrnEnabled?: boolean;
}

export const createTableStore = <T>(
  apiUrl: ROUTES,
  defaultColumns: Column[] = [],
  backendUrl?: string
) => {
  const initialColumns = defaultColumns.map(col => ({
    ...col,
    isActive: Boolean(col.isDefaultActive)
  }));

  const initialPagination = {
    page: 1,
    totalPages: 0,
    pageSize: 25,
    totalCount: 0,
    hasPrevious: false,
    hasNext: false
  };

  const initialParams = {
    page: apiUrl,
    pageNumber: "1",
    showPerPage: "25",
    selectedTableItem: null,
    contains: null,
    desktopDetailsSection: false,
    filters: null,
    sortedColumn: null,
    selectedTableItemUrlKey: null,
    hideFilters: false
  };

  const initialState = {
    data: [] as T[],
    columns: initialColumns,
    loading: false,
    noResults: false,
    initialized: false,
    initialLoading: true,
    inlineActions: [],
    rowSwitch: null,
    isCheckable: false,
    selectedRows: [],
    selectableRows: [],
    detailsData: null,
    pagination: initialPagination,
    params: initialParams,
    autoAssignVrnEnabled: false
  };

  const storeName = apiUrl.replace("/", ""); // "vehicles", "drivers"

  const filterOutRow = (selectedRows: string[], rowId: string) =>
    selectedRows.filter(id => id !== rowId);

  return create<TableStore<T>>()(
    persist<TableStore<T>>(
      (set, get) => ({
        ...initialState,

        setData: data => set({ data }),
        setLoading: loading => set({ loading }),
        setDetailsData: detailsData => set({ detailsData }),
        setParams: updates =>
          set(state => ({
            params: { ...state.params, ...updates }
          })),

        setDataAndPagination: (data, paginationData) =>
          set({
            data,
            pagination: {
              page: paginationData.page || 1,
              totalPages: paginationData.totalPages || 0,
              pageSize: paginationData.pageSize || 25,
              totalCount: paginationData.totalCount || 0,
              hasPrevious: paginationData.hasPrevious ?? false,
              hasNext: paginationData.hasNext ?? false
            },
            ...(paginationData?.autoAssignVrnEnabled !== undefined && {
              autoAssignVrnEnabled: paginationData?.autoAssignVrnEnabled
            })
          }),

        resetFiltersAndDetailsData: searchValue =>
          set(state => ({
            detailsData: null,
            params: {
              ...state.params,
              contains: searchValue,
              filters: null,
              desktopDetailsSection: false,
              selectedTableItem: null,
              selectedTableItemUrlKey: null
            }
          })),

        resetTableDetails: () =>
          set(state => ({
            detailsData: null,
            params: {
              ...state.params,
              desktopDetailsSection: false,
              selectedTableItem: null,
              selectedTableItemUrlKey: null
            }
          })),

        sortColumn: resource => {
          const { params } = get();
          const current = params.sortedColumn;

          let newSortedColumn: {
            orderBy: string;
            descending: boolean;
          } | null = {
            orderBy: resource,
            descending: true
          };

          if (current?.orderBy === resource) {
            if (current.descending) {
              newSortedColumn = { orderBy: resource, descending: false };
            } else {
              newSortedColumn = null;
            }
          }

          set(state => ({
            params: {
              ...state.params,
              sortedColumn: newSortedColumn
            }
          }));
        },

        setColumns: columns => set({ columns }),

        selectRow: rowId =>
          set(state => ({
            selectedRows: [...state.selectedRows, rowId]
          })),

        deselectRow: rowId =>
          set(state => ({
            selectedRows: filterOutRow(state.selectedRows, rowId)
          })),

        setSelectedRows: rowIds => set({ selectedRows: rowIds }),
        clearSelection: () => set({ selectedRows: [] }),
        setSelectableRows: rowIds => set({ selectableRows: rowIds }),
        setInlineActions: actions => set({ inlineActions: actions }),
        setRowSwitch: rowSwitch => set({ rowSwitch: rowSwitch }),
        setIsCheckable: checkable => set({ isCheckable: checkable }),
        resetInitialLoading: () => set({ initialLoading: true }),

        resetExceptPaginationAndColumns: () =>
          set(state => ({
            ...initialState,
            pagination: state.pagination,
            columns: state.columns,
            params: {
              ...initialParams,
              pageNumber: state.params.pageNumber,
              showPerPage: state.params.showPerPage,
              sortedColumn: state.params.sortedColumn
            }
          })),

        reset: () => set(initialState),

        hardReset: () => {
          set(initialState);
          localStorage.removeItem(`table-storage-${storeName}`);
        },

        closeDetails: () =>
          set(state => ({
            detailsData: null,
            params: {
              ...state.params,
              desktopDetailsSection: false,
              selectedTableItem: null,
              selectedTableItemUrlKey: null
            }
          })),

        fetchData: async (getData, handleResponse) => {
          const { params } = get();
          set({ loading: true });

          const setResetDetailsSection =
            useDetailsStore.getState().setResetDetailsSection;

          const { getFiltersAndFormFilterQueryParams } =
            useTableDataFunctions();

          const queryParams = buildQueryString({
            PageNumber: params.pageNumber,
            PageSize: params.showPerPage,
            Contains: params.contains?.trim(),
            OrderBy: params.sortedColumn?.orderBy,
            Descending: params.sortedColumn?.descending
          });

          const filterParams = getFiltersAndFormFilterQueryParams(
            params.filters
          );

          try {
            const isCompanies = apiUrl === ROUTES.Companies;
            const requestUrl = backendUrl ?? apiUrl;

            const fullUrl = isCompanies
              ? requestUrl
              : `${requestUrl}?${queryParams}${filterParams}`;

            const response = await getData(fullUrl);

            const items = response?.data?.items ?? [];
            const paginationData = response?.data ?? {};

            const autoVrnStatus = response?.data?.autoAssignVrnEnabled;
            const vrnStatusExists = autoVrnStatus !== undefined;

            const autoAssignVrnVehicles = vrnStatusExists
              ? autoVrnStatus
              : undefined;

            const dataForMap = isCompanies ? paginationData : items;

            const tableData = mappedDataByTableRoute(apiUrl, dataForMap);
            const totalCount = isCompanies
              ? tableData.length
              : (paginationData.totalCount ?? 0);

            set({
              data: tableData,
              pagination: {
                page: paginationData.page || 1,
                totalPages: paginationData.totalPages || 0,
                pageSize: paginationData.pageSize || 25,
                totalCount: totalCount,
                hasPrevious: paginationData.hasPrevious ?? false,
                hasNext: paginationData.hasNext ?? false
              },
              loading: false,
              initialLoading: false,
              noResults: items.length === 0,
              initialized: true,
              ...(vrnStatusExists && {
                autoAssignVrnEnabled: autoAssignVrnVehicles
              })
            });

            setResetDetailsSection(false);
          } catch (error) {
            const axiosError = error as AxiosError;
            axiosError.message &&
              handleResponse(ALERT_STATUS.Critical, axiosError.message);

            set({ loading: false, initialLoading: false });
            setResetDetailsSection(false);
          }
        }
      }),
      {
        name: `table-storage-${storeName}`,
        storage: createJSONStorage(() => localStorage),
        partialize: (state): any => {
          const columnsActiveState = state.columns.reduce(
            (acc, col) => {
              acc[col.name.toLowerCase()] = col.isActive;
              return acc;
            },
            {} as Record<string, boolean | undefined>
          );

          return {
            columns: columnsActiveState,
            params: {
              pageNumber: state.params.pageNumber,
              showPerPage: state.params.showPerPage,
              sortedColumn: state.params.sortedColumn,
              filters: state.params.filters,
              contains: state.params.contains,
              selectedTableItem: state.params.selectedTableItem,
              selectedTableItemUrlKey: state.params.selectedTableItemUrlKey,
              desktopDetailsSection: state.params.desktopDetailsSection
            }
          };
        },
        merge: (persistedState: any, currentState) => {
          const mergedColumns = currentState.columns.map(col => ({
            ...col,
            isActive:
              persistedState.columns?.[col.name.toLowerCase()] ?? col.isActive
          }));

          const mergedParams = {
            ...currentState.params,
            ...persistedState.params
          };

          return {
            ...currentState,
            columns: mergedColumns,
            params: mergedParams
          };
        },
        version: 1
      }
    )
  );
};

export const useDashboardTableStore = createTableStore<any>(
  ROUTES.Dashboard,
  []
);

export const useVehiclesTableStore = createTableStore<any>(
  ROUTES.Vehicles,
  VEHICLES_TABLE_COLUMNS
);

export const useDriversTableStore = createTableStore<any>(
  ROUTES.Drivers,
  DRIVERS_TABLE_COLUMNS
);

export const useVehicleFilesTableStore = createTableStore<any>(
  ROUTES.VehicleFiles,
  VEHICLES_FILES_TABLE_COLUMNS
);

export const useDriverFilesTableStore = createTableStore<any>(
  ROUTES.DriverFiles,
  DRIVERS_FILES_TABLE_COLUMNS
);

export const useCompaniesTableStore = createTableStore<any>(
  ROUTES.Companies,
  COMPANIES_TABLE_COLUMNS.desktop // desktop default
);

export const useUsersTableStore = createTableStore<any>(
  ROUTES.Users,
  USERS_TABLE_COLUMNS
);

export const useUsersInvitationsTableStore = createTableStore<any>(
  ROUTES.UsersInvitations,
  USERS_INVITATIONS_TABLE_COLUMNS
);

export const useIntegrationsTableStore = createTableStore<any>(
  ROUTES.Integrations,
  INTEGRATIONS_TABLE_COLUMNS
);

export const useIntegrationsApiTableStore = createTableStore<any>(
  ROUTES.IntegrationsApi,
  INTEGRATIONS_API_TABLE_COLUMNS,
  ApiResources.IntegrationsApiCredentials
);

export const useAnalysisProvidersTableStore = createTableStore<any>(
  ROUTES.AnalysisProviders,
  ANALYSIS_PROVIDER_TABLE_COLUMNS,
  ApiResources.IntegrationsAnalysisProviders
);

export const getTableStoreByRoute = (name: ROUTES) => {
  switch (name) {
    case ROUTES.Vehicles:
      return useVehiclesTableStore;

    case ROUTES.Drivers:
      return useDriversTableStore;

    case ROUTES.VehicleFiles:
      return useVehicleFilesTableStore;

    case ROUTES.DriverFiles:
      return useDriverFilesTableStore;

    case ROUTES.Companies:
      return useCompaniesTableStore;

    case ROUTES.Users:
      return useUsersTableStore;

    case ROUTES.UsersInvitations:
      return useUsersInvitationsTableStore;

    case ROUTES.Integrations:
      return useIntegrationsTableStore;

    case ROUTES.IntegrationsApi:
      return useIntegrationsApiTableStore;

    case ROUTES.AnalysisProviders:
      return useAnalysisProvidersTableStore;

    case ROUTES.Dashboard:
      return useDashboardTableStore;

    default:
      return useVehiclesTableStore;
  }
};

export const useTableStoreByRoute = <SelectedState = TableStore<any>>(
  name: ROUTES,
  selector: (state: TableStore<any>) => SelectedState = state =>
    state as SelectedState
) => getTableStoreByRoute(name)(selector);

export const resetAllTableStores = () => {
  useVehiclesTableStore.getState().hardReset();
  useDriversTableStore.getState().hardReset();
  useVehicleFilesTableStore.getState().hardReset();
  useDriverFilesTableStore.getState().hardReset();
  useCompaniesTableStore.getState().hardReset();
  useUsersTableStore.getState().hardReset();
  useUsersInvitationsTableStore.getState().hardReset();
  useIntegrationsTableStore.getState().hardReset();
  useIntegrationsApiTableStore.getState().hardReset();
  useAnalysisProvidersTableStore.getState().hardReset();
};

const stores = [
  useVehiclesTableStore,
  useDriversTableStore,
  useCompaniesTableStore,
  useVehicleFilesTableStore,
  useDriverFilesTableStore,
  useUsersTableStore,
  useUsersInvitationsTableStore,
  useIntegrationsTableStore,
  useIntegrationsApiTableStore,
  useAnalysisProvidersTableStore
];

export const resetAllTablesExceptPaginationAndColumns = () => {
  for (const store of stores) {
    store.getState().resetExceptPaginationAndColumns();
  }
};

export const resetAllTableStoreFiltersAndDetailsData = () => {
  for (const store of stores) {
    store.getState().resetFiltersAndDetailsData(null);
  }
};

export const resetAllTableStoreFilters = () => {
  for (const store of stores) {
    const state = store.getState();
    state.setParams({ filters: null, contains: null });
  }
};

export const resetAllTableStoreSearches = () => {
  for (const store of stores) {
    const state = store.getState();
    state.setParams({ contains: null });
  }
};

export const resetAllTableStoreDetails = () => {
  for (const store of stores) {
    store.getState().resetTableDetails();
  }
};

export const resetAllTableStoreCurrentPage = () => {
  for (const store of stores) {
    const state = store.getState();
    state.setParams({ pageNumber: "1" });
  }
};

export const mappedDataByTableRoute = (route: ROUTES, data: any) => {
  switch (route) {
    case ROUTES.Vehicles:
      return mapVehiclesTableRowData(data);
    case ROUTES.Drivers:
      return mapDriversTableRowData(data);
    case ROUTES.VehicleFiles:
      return mapVehicleFilesTableRowData(data);
    case ROUTES.DriverFiles:
      return mapDriversFilesTableRowData(data);
    case ROUTES.Companies:
      return mapCompaniesTableRowData(data);

    case ROUTES.Users:
      return mapUserTableRowData(data);
    case ROUTES.UsersInvitations:
      return mapUsersInvitationsTableRowData(data);

    case ROUTES.Integrations:
      return mapIntegrationsTableRowData(data);

    case ROUTES.IntegrationsApi:
      return mapIntegrationsApiTableRowData(data);

    case ROUTES.AnalysisProviders:
      return mapAnalysisProvidersTableRowData(data);

    default:
      return mapVehiclesTableRowData(data);
  }
};
